// @ts-nocheck
import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, DollarSign, TrendingUp, PiggyBank, Percent } from "lucide-react";
import { useUpcomingEventsForTravel, useTravelSummary, useTravelBudgets } from "@/hooks/useTravelData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const CATEGORIES = ["flights", "hotels", "rental_cars", "gas", "rideshare", "per_diem", "other"];

const BudgetTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForTravel();
  const queryClient = useQueryClient();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: summary, isLoading: sumLoading } = useTravelSummary(selectedEventId);
  const { data: budgets } = useTravelBudgets(selectedEventId);

  const [budgetValues, setBudgetValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Initialize budget values from fetched data
  useEffect(() => {
    if (budgets) {
      const vals: Record<string, string> = {};
      budgets.forEach((b: any) => { vals[b.category] = b.budgeted_amount?.toString() || ""; });
      setBudgetValues(vals);
    }
  }, [budgets]);

  const summaryMap = useMemo(() => {
    const map: Record<string, any> = {};
    (summary || []).forEach((s: any) => { map[s.category] = s; });
    return map;
  }, [summary]);

  const totals = useMemo(() => {
    let budgeted = 0, actual = 0;
    CATEGORIES.forEach(cat => {
      budgeted += parseFloat(budgetValues[cat] || "0") || 0;
      actual += Number(summaryMap[cat]?.actual_amount) || 0;
    });
    return { budgeted, actual, remaining: budgeted - actual, pct: budgeted > 0 ? Math.round((actual / budgeted) * 100) : 0 };
  }, [budgetValues, summaryMap]);

  const handleSave = async () => {
    if (!selectedEventId) return;
    setSaving(true);
    try {
      const upserts = CATEGORIES.filter(cat => budgetValues[cat]).map(cat => ({
        event_id: selectedEventId,
        category: cat,
        budgeted_amount: parseFloat(budgetValues[cat]) || 0,
      }));
      if (upserts.length > 0) {
        const { error } = await supabase.from("manage_travel_budgets").upsert(upserts, { onConflict: "event_id,category" });
        if (error) throw error;
      }
      toast.success("Budgets saved");
      queryClient.invalidateQueries({ queryKey: ["travel-budgets", selectedEventId] });
      queryClient.invalidateQueries({ queryKey: ["travel-summary", selectedEventId] });
    } catch {
      toast.error("Failed to save budgets");
    } finally {
      setSaving(false);
    }
  };

  const getProgressColor = (pct: number) => {
    if (pct > 100) return "bg-red-500";
    if (pct > 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="min-w-[280px]">
        <Label>Select Event</Label>
        <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
          <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
          <SelectContent>
            {(events || []).map((e: any) => (
              <SelectItem key={e.id} value={e.id}>{e.name} ({format(parseLocalDate(e.start_date), "MMM d")})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEventId && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-3 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-primary" />
              <div><p className="text-xl font-bold">${totals.budgeted.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Budget</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-500" />
              <div><p className="text-xl font-bold">${totals.actual.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Spent</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <PiggyBank className={`h-6 w-6 ${totals.remaining >= 0 ? "text-green-500" : "text-red-500"}`} />
              <div><p className={`text-xl font-bold ${totals.remaining < 0 ? "text-red-500" : ""}`}>${totals.remaining.toLocaleString()}</p><p className="text-xs text-muted-foreground">Remaining</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Percent className="h-6 w-6 text-secondary" />
              <div><p className="text-xl font-bold">{totals.pct}%</p><p className="text-xs text-muted-foreground">Budget Used</p></div>
            </CardContent></Card>
          </div>

          {sumLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Budgeted</TableHead>
                        <TableHead className="text-right">Actual Spent</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead className="text-right"># Bookings</TableHead>
                        <TableHead className="text-right">Still Needed</TableHead>
                        <TableHead>% Spent</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {CATEGORIES.map(cat => {
                        const s = summaryMap[cat] || {};
                        const budgeted = parseFloat(budgetValues[cat] || "0") || 0;
                        const actual = Number(s.actual_amount) || 0;
                        const remaining = budgeted - actual;
                        const pct = budgeted > 0 ? Math.round((actual / budgeted) * 100) : 0;
                        return (
                          <TableRow key={cat}>
                            <TableCell className="font-medium capitalize">{cat.replace("_", " ")}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.01"
                                className="w-[120px] ml-auto text-right"
                                value={budgetValues[cat] || ""}
                                onChange={(e) => setBudgetValues({ ...budgetValues, [cat]: e.target.value })}
                                placeholder="$0"
                              />
                            </TableCell>
                            <TableCell className="text-right">${actual.toLocaleString()}</TableCell>
                            <TableCell className={`text-right ${remaining < 0 ? "text-red-500 font-semibold" : ""}`}>${remaining.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{Number(s.booking_count) || 0}</TableCell>
                            <TableCell className="text-right">{Number(s.still_needed) || 0}</TableCell>
                            <TableCell className="w-[140px]">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full rounded-full ${getProgressColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                                <span className="text-xs w-[35px] text-right">{pct}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="font-bold">Totals</TableCell>
                        <TableCell className="text-right font-bold">${totals.budgeted.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-bold">${totals.actual.toLocaleString()}</TableCell>
                        <TableCell className={`text-right font-bold ${totals.remaining < 0 ? "text-red-500" : ""}`}>${totals.remaining.toLocaleString()}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                              <div className={`h-full rounded-full ${getProgressColor(totals.pct)}`} style={{ width: `${Math.min(totals.pct, 100)}%` }} />
                            </div>
                            <span className="text-xs w-[35px] text-right">{totals.pct}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
                <div className="p-4 flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Budget</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default BudgetTab;
